import { LayoutMode, LayoutProps } from 'components/types';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAllMerchants } from "state/ducks/merchants/slice";
import { selectStringTranslator } from 'state/ducks/translator-strings/slice';
import {
    BuyingOption,
    DeliveryPromiseTime,
    StateCode,
    Variation,
    VisitType,
    BUY_BOX_PAGE_TYPE
} from 'state/types';
import DeliveryPromiseUtils from 'state/utils/DeliveryPromiseUtils';
import {BuyboxCSAEvent, CSA, TranslatorStringIds, isTreatmentT1, CDP_GET_STARTED_BUTTON_WEBLAB} from 'utils';
import BuyingOptionWidget from "./buyingoption";
import InitialContext from 'state/static-data/InitialContext';
import "./index.scss";

export interface OffersWidgetProps extends LayoutProps {
    readonly variation?: Variation,
    readonly buyingOptions: BuyingOption[],
    readonly merchantDeliveryPromises: DeliveryPromiseTime[],
    readonly selectedStateCode: StateCode;
}

const OffersWidget = ({ variation, buyingOptions, merchantDeliveryPromises, selectedStateCode, layoutMode }: OffersWidgetProps) => {
    const t = useSelector(selectStringTranslator);
    const merchants = useSelector(selectAllMerchants);
    const [selectedBuyingOption, setSelectedBuyingOption] = useState<BuyingOption>();
    const buyboxButtonRef = useRef();

    const setUrlParams = (buyingOption: BuyingOption, visitType: VisitType, selectedStateCode: StateCode) => {
        const currentURL = new URL(window.location.href);
        currentURL.searchParams.set("visitType", visitType);
        currentURL.searchParams.set("location", selectedStateCode);
        currentURL.searchParams.set("obfuscatedMerchantId", buyingOption.obfuscatedMerchantId);
        window.history.replaceState(null, "", currentURL);
    }

    useEffect(() => {
        const buyboxButton = buyboxButtonRef.current as any;
        if (!!selectedBuyingOption) {
            const isClinicOpen = DeliveryPromiseUtils.checkIfClinicOpen(selectedBuyingOption.obfuscatedMerchantId, merchantDeliveryPromises);
            if (!isClinicOpen) {
                setSelectedBuyingOption(undefined)
                buyboxButton?.disable();
            }
        }
    }, [merchantDeliveryPromises]);

    useEffect(() => {
        const buyboxButton = buyboxButtonRef.current as any;
        const buyableBuyingOptions = buyingOptions.filter(bo => DeliveryPromiseUtils.checkIfClinicOpen(bo.obfuscatedMerchantId, merchantDeliveryPromises))
        if (buyableBuyingOptions.length === 1) {
            setSelectedBuyingOption(buyableBuyingOptions[0])
            buyboxButton?.enable();
        } else {
            setSelectedBuyingOption(undefined)
            buyboxButton?.disable();
        }
    }, [buyingOptions]);

    useEffect(() => {
        if (!!variation && layoutMode === LayoutMode.DESKTOP) {
            const event: BuyboxCSAEvent = {
                asin: variation.asin,
                offers: buyingOptions.map(bo => bo.obfuscatedMerchantId).join(','),
                topRatedOffer: buyingOptions[0]?.obfuscatedMerchantId ?? '',
                stateCode: selectedStateCode
            }
            CSA.emitBuyboxCSA(event)
        }
        if (!!variation && !!selectedBuyingOption) {
            setUrlParams(selectedBuyingOption, variation.visitType, selectedStateCode);
        }
    }, [selectedBuyingOption]);

    const onBuyingOptionSelected = (buyingOption: BuyingOption, visitType: VisitType, selectedStateCode: StateCode) => {
        CSA.buyingOptionReloaded();
        setSelectedBuyingOption(buyingOption);
        setUrlParams(buyingOption, visitType, selectedStateCode);
        const buyboxButton = buyboxButtonRef.current as any;
        buyboxButton?.enable();
    }

    const onBuyboxButtonClick = () => {
        if (!!selectedBuyingOption) {
            const url = new URL(selectedBuyingOption.ctaUrl);
            url.searchParams.set('location', selectedStateCode);
            window.location.href = url.href;
        }
    }

    const generateBuyingOptionElements = () => {
        const elements: JSX.Element[] = [];
        if (!!variation) {
            buyingOptions.forEach((bo, index) => {
                index > 0 && elements.push(<pui-divider key={`bo-divider-${index}`} spacingTop="medium" spacingBottom="medium" />)
                const merchant = merchants.find(m => m.obfuscatedMerchantId === bo.obfuscatedMerchantId);
                if (!merchant) {
                    return <></>
                }
                const deliveryPromiseTime = merchantDeliveryPromises.find(dp => dp.obfuscatedMerchantId === merchant.obfuscatedMerchantId);

                elements.push(<BuyingOptionWidget
                    key={`bo-${bo.obfuscatedMerchantId}`}
                    selectedStateCode={selectedStateCode}
                    visitType={variation.visitType}
                    merchant={merchant}
                    buyingOption={bo}
                    deliveryPromiseTime={deliveryPromiseTime}
                    onClickCallback={onBuyingOptionSelected}
                    selected={bo.obfuscatedMerchantId === selectedBuyingOption?.obfuscatedMerchantId} />);
            });
        }
        return elements;
    }

    const buyboxContinueButtonText = InitialContext.getPageType() === BUY_BOX_PAGE_TYPE.DETAIL_PAGE
        ? TranslatorStringIds.ChooseOfferBuyBoxButtonLabel
        : TranslatorStringIds.OfferBoxButtonText

    return (
        <pui-section class={"offers-box" + (InitialContext.isKyanite() ? ' kyanite' : '')}>
            {!InitialContext.isKyanite() && buyingOptions.length > 0 && <>
                <pui-divider spacingTop="medium" />
                <pui-section spacingTop="medium" spacingBottom="small">
                    <pui-heading input={t.resolve(TranslatorStringIds.ChooseOfferHead)}
                        spacingBottom="small"></pui-heading>
                    <div className="pui-text medium-font normal-font-weight wrap-overflow">
                        <pui-text input={t.resolve(TranslatorStringIds.ChooseOfferDescription)}></pui-text>
                    </div>
                </pui-section>
                <pui-section>
                    {generateBuyingOptionElements()}
                </pui-section>
            </>}
            <pui-button
                label={
                    isTreatmentT1(CDP_GET_STARTED_BUTTON_WEBLAB)
                        ? t.resolve(TranslatorStringIds.StartIntakeFormButtonText)
                        : t.resolve(buyboxContinueButtonText)
                }
                textSize="large"
                ref={buyboxButtonRef}
                onClick={() => onBuyboxButtonClick()}
            ></pui-button>
        </pui-section>
    )
}
export default OffersWidget;